import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Sprout, Plus, Play, Check, X, Snowflake, RefreshCw, Edit, Trash2, ShieldAlert } from 'lucide-react';

const EMPTY_PLAN = {
  name: '', description: '', currency: '', growth_type: 'compound',
  annual_growth_rate: 0.10, min_deposit: 0, max_deposit: 0,
  min_balance: 0, max_balance: 0, lock_days: 0, cooling_off_hours: 24,
  early_withdrawal_penalty_pct: 0, withdrawal_fee_pct: 0, auto_renewal: false,
};

const STATUS_STYLES = {
  active: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  paused: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  archived: 'bg-muted text-muted-foreground border-border',
  pending_cooling: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  under_review: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
  approved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
  cancelled: 'bg-muted text-muted-foreground border-border',
  matured: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  closed: 'bg-muted text-muted-foreground border-border',
  frozen: 'bg-red-500/15 text-red-600 border-red-500/30',
};

export const AdminSavingsPage = () => {
  const [tab, setTab] = useState('plans');
  const [currencies, setCurrencies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlan, setShowPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [busy, setBusy] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ type: 'bonus', amount: '', reason: '' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, p, a, w] = await Promise.all([
        publicAPI.getCurrencies(true),
        adminAPI.listSavingsPlans(),
        adminAPI.listSavingsAccounts(),
        adminAPI.listSavingsWithdrawals(),
      ]);
      setCurrencies(c.data);
      setPlans(p.data);
      setAccounts(a.data);
      setWithdrawals(w.data);
    } catch (e) {
      toast.error('Failed to load savings data');
    } finally { setLoading(false); }
  };

  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm({ ...EMPTY_PLAN, currency: currencies[0]?.symbol || '' });
    setShowPlan(true);
  };

  const openEditPlan = (p) => {
    setEditingPlan(p);
    setPlanForm({ ...p });
    setShowPlan(true);
  };

  const savePlan = async () => {
    if (!planForm.name || !planForm.currency) return toast.error('Name and currency required');
    setBusy(true);
    try {
      if (editingPlan) {
        await adminAPI.updateSavingsPlan(editingPlan.id, planForm);
        toast.success('Plan updated');
      } else {
        await adminAPI.createSavingsPlan(planForm);
        toast.success('Plan created');
      }
      setShowPlan(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed');
    } finally { setBusy(false); }
  };

  const togglePlanStatus = async (p) => {
    const newStatus = p.status === 'active' ? 'paused' : 'active';
    try {
      await adminAPI.updateSavingsPlan(p.id, { status: newStatus });
      toast.success(`Plan ${newStatus}`);
      loadAll();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const archivePlan = async (p) => {
    if (!window.confirm(`Archive plan "${p.name}"? Users cannot open new accounts under it.`)) return;
    try {
      await adminAPI.archiveSavingsPlan(p.id);
      toast.success('Plan archived');
      loadAll();
    } catch (e) { toast.error('Failed'); }
  };

  const approveWithdrawal = async (w) => {
    if (!window.confirm(`Approve withdrawal of ${w.gross_amount} ${w.currency} for ${w.user_email}? Net ${w.net_amount.toFixed(2)} will be credited.`)) return;
    try {
      await adminAPI.approveSavingsWithdrawal(w.id);
      toast.success('Approved');
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const holdWithdrawal = async (w) => {
    const reason = window.prompt('Reason for placing under review?');
    if (!reason) return;
    try {
      await adminAPI.holdSavingsWithdrawal(w.id, reason);
      toast.success('Placed under review');
      loadAll();
    } catch (e) { toast.error('Failed'); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return toast.error('Reason required');
    try {
      await adminAPI.rejectSavingsWithdrawal(rejectFor.id, rejectReason.trim());
      toast.success('Rejected');
      setRejectFor(null);
      setRejectReason('');
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const doAdjust = async () => {
    if (!adjustForm.amount || !adjustForm.reason.trim()) return toast.error('Amount and reason required');
    try {
      await adminAPI.adjustSavingsAccount(adjustFor.id, {
        type: adjustForm.type,
        amount: parseFloat(adjustForm.amount),
        reason: adjustForm.reason.trim(),
      });
      toast.success('Adjustment applied');
      setAdjustFor(null);
      setAdjustForm({ type: 'bonus', amount: '', reason: '' });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const freezeAccount = async (a) => {
    if (!window.confirm(`${a.status === 'frozen' ? 'Unfreeze' : 'Freeze'} account?`)) return;
    try {
      await adminAPI.freezeSavingsAccount(a.id);
      toast.success('Done');
      loadAll();
    } catch (e) { toast.error('Failed'); }
  };

  const runGrowth = async () => {
    try {
      const res = await adminAPI.runSavingsGrowth();
      toast.success(`Grown ${res.data.grown} accounts (+${res.data.total_growth.toFixed(4)})`);
      loadAll();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-savings-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-semibold flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Sprout className="h-8 w-8 text-emerald-600" /> Smart Growth Savings
            </h1>
            <p className="mt-2 text-muted-foreground">Manage plans, savings accounts and withdrawal requests</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={runGrowth} data-testid="run-growth-btn"><RefreshCw className="h-4 w-4 mr-2" /> Run Growth</Button>
            <Button onClick={openCreatePlan} data-testid="new-plan-btn"><Plus className="h-4 w-4 mr-2" /> New Plan</Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="plans" data-testid="tab-plans">Plans ({plans.length})</TabsTrigger>
            <TabsTrigger value="accounts" data-testid="tab-accounts">Accounts ({accounts.length})</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">
              Withdrawals ({withdrawals.length})
              {withdrawals.length > 0 && <span className="ml-2 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plans">
            <Card>
              <CardContent className="pt-6">
                {loading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> : plans.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No plans yet. Create your first one.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>APY</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Lock</TableHead>
                        <TableHead>Cooling</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plans.map(p => (
                        <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell>{p.currency}</TableCell>
                          <TableCell>{(p.annual_growth_rate * 100).toFixed(2)}%</TableCell>
                          <TableCell className="capitalize">{p.growth_type}</TableCell>
                          <TableCell>{p.lock_days} days</TableCell>
                          <TableCell>{p.cooling_off_hours}h</TableCell>
                          <TableCell><Badge variant="outline" className={STATUS_STYLES[p.status]}>{p.status}</Badge></TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => togglePlanStatus(p)} title={p.status === 'active' ? 'Pause' : 'Resume'} data-testid={`toggle-plan-${p.id}`}>
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => openEditPlan(p)} data-testid={`edit-plan-${p.id}`}><Edit className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => archivePlan(p)} data-testid={`archive-plan-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="accounts">
            <Card>
              <CardContent className="pt-6">
                {accounts.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No savings accounts yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead>Growth</TableHead>
                        <TableHead>Days</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accounts.map(a => (
                        <TableRow key={a.id} data-testid={`account-row-${a.id}`}>
                          <TableCell className="text-sm">{a.user_email}</TableCell>
                          <TableCell>{a.plan_name}</TableCell>
                          <TableCell>{a.currency}</TableCell>
                          <TableCell className="font-mono">{a.balance.toFixed(4)}</TableCell>
                          <TableCell className="font-mono text-emerald-600">+{a.total_growth_earned.toFixed(4)}</TableCell>
                          <TableCell>{a.days_saved}</TableCell>
                          <TableCell><Badge variant="outline" className={STATUS_STYLES[a.status]}>{a.status}</Badge></TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => setAdjustFor(a)} title="Adjust" data-testid={`adjust-account-${a.id}`}><ShieldAlert className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => freezeAccount(a)} title="Freeze/unfreeze" data-testid={`freeze-account-${a.id}`}><Snowflake className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="withdrawals">
            <Card>
              <CardContent className="pt-6">
                {withdrawals.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No pending withdrawals.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Gross</TableHead>
                        <TableHead>Fee</TableHead>
                        <TableHead>Penalty</TableHead>
                        <TableHead>Net</TableHead>
                        <TableHead>Cooling ends</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {withdrawals.map(w => (
                        <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                          <TableCell className="text-sm">{w.user_email}</TableCell>
                          <TableCell className="font-mono">{w.gross_amount} {w.currency}</TableCell>
                          <TableCell className="font-mono">{w.fee.toFixed(2)}</TableCell>
                          <TableCell className="font-mono">{w.penalty.toFixed(2)}</TableCell>
                          <TableCell className="font-mono font-medium">{w.net_amount.toFixed(2)}</TableCell>
                          <TableCell className="text-xs">{formatDate(w.cooling_off_ends_at)}</TableCell>
                          <TableCell><Badge variant="outline" className={STATUS_STYLES[w.status]}>{w.status.replace('_', ' ')}</Badge></TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="sm" onClick={() => approveWithdrawal(w)} data-testid={`approve-w-${w.id}`}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                            <Button size="sm" variant="outline" onClick={() => holdWithdrawal(w)} data-testid={`hold-w-${w.id}`}>Hold</Button>
                            <Button size="sm" variant="destructive" onClick={() => setRejectFor(w)} data-testid={`reject-w-${w.id}`}><X className="h-3 w-3 mr-1" /> Reject</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Plan create/edit dialog */}
      <Dialog open={showPlan} onOpenChange={setShowPlan}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'New Savings Plan'}</DialogTitle>
            <DialogDescription>Set growth rate, deposit limits and cooling-off rules for this plan.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Plan Name</Label>
              <Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} data-testid="plan-name-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={2} value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={planForm.currency} onValueChange={(v) => setPlanForm({ ...planForm, currency: v })} disabled={!!editingPlan}>
                <SelectTrigger data-testid="plan-currency-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol} — {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Growth Type</Label>
              <Select value={planForm.growth_type} onValueChange={(v) => setPlanForm({ ...planForm, growth_type: v })}>
                <SelectTrigger data-testid="plan-growth-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="compound">Compound (recommended)</SelectItem>
                  <SelectItem value="simple">Simple</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Annual Growth Rate (0.10 = 10%)</Label>
              <Input type="number" step="0.001" value={planForm.annual_growth_rate} onChange={(e) => setPlanForm({ ...planForm, annual_growth_rate: parseFloat(e.target.value) || 0 })} data-testid="plan-rate-input" />
            </div>
            <div>
              <Label>Cooling-off (hours)</Label>
              <Input type="number" value={planForm.cooling_off_hours} onChange={(e) => setPlanForm({ ...planForm, cooling_off_hours: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Min Deposit</Label>
              <Input type="number" step="0.01" value={planForm.min_deposit} onChange={(e) => setPlanForm({ ...planForm, min_deposit: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Max Deposit (0 = none)</Label>
              <Input type="number" step="0.01" value={planForm.max_deposit} onChange={(e) => setPlanForm({ ...planForm, max_deposit: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Min Balance</Label>
              <Input type="number" step="0.01" value={planForm.min_balance} onChange={(e) => setPlanForm({ ...planForm, min_balance: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Max Balance (0 = none)</Label>
              <Input type="number" step="0.01" value={planForm.max_balance} onChange={(e) => setPlanForm({ ...planForm, max_balance: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Lock Period (days, 0 = flexible)</Label>
              <Input type="number" value={planForm.lock_days} onChange={(e) => setPlanForm({ ...planForm, lock_days: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Early Withdrawal Penalty (0.05 = 5%)</Label>
              <Input type="number" step="0.001" value={planForm.early_withdrawal_penalty_pct} onChange={(e) => setPlanForm({ ...planForm, early_withdrawal_penalty_pct: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Withdrawal Fee (0.01 = 1%)</Label>
              <Input type="number" step="0.001" value={planForm.withdrawal_fee_pct} onChange={(e) => setPlanForm({ ...planForm, withdrawal_fee_pct: parseFloat(e.target.value) || 0 })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlan(false)}>Cancel</Button>
            <Button onClick={savePlan} disabled={busy} data-testid="save-plan-btn">{busy ? 'Saving...' : editingPlan ? 'Update Plan' : 'Create Plan'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject withdrawal dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Withdrawal</DialogTitle>
            <DialogDescription>The user will be notified. This action is logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} data-testid="confirm-reject-btn">Reject Withdrawal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust account dialog */}
      <Dialog open={!!adjustFor} onOpenChange={(v) => !v && setAdjustFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Savings Account</DialogTitle>
            <DialogDescription>Credit a bonus, apply a penalty, or reverse a transaction. All adjustments are logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={adjustForm.type} onValueChange={(v) => setAdjustForm({ ...adjustForm, type: v })}>
                <SelectTrigger data-testid="adjust-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">Bonus (credit)</SelectItem>
                  <SelectItem value="penalty">Penalty (debit)</SelectItem>
                  <SelectItem value="reversal">Reversal (signed)</SelectItem>
                  <SelectItem value="adjustment">Adjustment (signed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input type="number" step="0.01" value={adjustForm.amount} onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })} data-testid="adjust-amount-input" />
            </div>
            <div>
              <Label>Reason (required)</Label>
              <Textarea rows={2} value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} data-testid="adjust-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustFor(null)}>Cancel</Button>
            <Button onClick={doAdjust} data-testid="confirm-adjust-btn">Apply Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
